/**
 * Report Templates dialog — manage reusable canned inputs + commentary +
 * email message for wage reports. Mount this inside WageReports page as
 * a modal; it owns its own CRUD state.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, Star, StarOff, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = {
    name: "",
    description: "",
    hours_per_week: 40,
    market_hourly_rate: 52,
    retained_profit_pct: 0.20,
    employer_oncost_pct: 0.035,
    pay_weeks_per_year: 52,
    avg_monthly_fee_per_client: null,
    min_cash_buffer: null,
    commentary: "",
    email_message: "",
    is_default: false,
};

export default function ReportTemplatesDialog({ open, onClose, onChanged }) {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // either null (list view) or template object (editor view)
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        try { const { data } = await api.get("/wage-report-templates"); setList(data || []); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { if (open) { load(); setEditing(null); } }, [open]);

    async function save() {
        setSaving(true);
        try {
            const body = { ...editing };
            // Normalise number-ish fields
            ["hours_per_week", "market_hourly_rate", "retained_profit_pct", "employer_oncost_pct",
             "pay_weeks_per_year", "avg_monthly_fee_per_client", "min_cash_buffer"].forEach((k) => {
                const v = body[k];
                body[k] = v === "" || v == null ? null : Number(v);
            });
            if (editing.id) await api.patch(`/wage-report-templates/${editing.id}`, body);
            else await api.post("/wage-report-templates", body);
            toast.success("Template saved");
            onChanged?.();
            await load();
            setEditing(null);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }

    async function remove(tpl) {
        if (!window.confirm(`Delete template "${tpl.name}"? This can't be undone.`)) return;
        try {
            await api.delete(`/wage-report-templates/${tpl.id}`);
            toast.success("Template deleted");
            onChanged?.();
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-2xl" data-testid="templates-dialog">
                <DialogHeader>
                    <DialogTitle>{editing ? (editing.id ? "Edit template" : "New template") : "Report templates"}</DialogTitle>
                </DialogHeader>

                {editing ? (
                    <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                        <div>
                            <Label>Name</Label>
                            <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="tpl-name" />
                        </div>
                        <div>
                            <Label>Short description</Label>
                            <Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="e.g. Sole trader · 40 hrs · 20% retained" />
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-[12.5px]">
                            <TplNum label="Hours / week" value={editing.hours_per_week} onChange={(v) => setEditing({ ...editing, hours_per_week: v })} testid="tpl-hours" />
                            <TplNum label="Market $/hour" value={editing.market_hourly_rate} onChange={(v) => setEditing({ ...editing, market_hourly_rate: v })} testid="tpl-rate" />
                            <TplNum label="Retained %" value={editing.retained_profit_pct != null ? +(Number(editing.retained_profit_pct) * 100).toFixed(2) : null}
                                onChange={(v) => setEditing({ ...editing, retained_profit_pct: v == null ? null : Number(v) / 100 })} suffix="%" testid="tpl-retained" />
                            <TplNum label="Employer on-cost %" value={editing.employer_oncost_pct != null ? +(Number(editing.employer_oncost_pct) * 100).toFixed(2) : null}
                                onChange={(v) => setEditing({ ...editing, employer_oncost_pct: v == null ? null : Number(v) / 100 })} suffix="%" />
                            <TplNum label="Pay weeks / year" value={editing.pay_weeks_per_year} onChange={(v) => setEditing({ ...editing, pay_weeks_per_year: v })} />
                            <TplNum label="Avg fee / client" value={editing.avg_monthly_fee_per_client} onChange={(v) => setEditing({ ...editing, avg_monthly_fee_per_client: v })} />
                            <TplNum label="Min cash buffer" value={editing.min_cash_buffer} onChange={(v) => setEditing({ ...editing, min_cash_buffer: v })} />
                        </div>
                        <div>
                            <Label>Canned notes shown in the PDF / portal view</Label>
                            <Textarea rows={4} value={editing.commentary || ""} onChange={(e) => setEditing({ ...editing, commentary: e.target.value })}
                                placeholder="e.g. These numbers reflect our mid-year review. We'll revisit after Q3 BAS." data-testid="tpl-commentary" />
                        </div>
                        <div>
                            <Label>Canned email message (shown in Send dialog)</Label>
                            <Textarea rows={4} value={editing.email_message || ""} onChange={(e) => setEditing({ ...editing, email_message: e.target.value })}
                                placeholder="e.g. Hi, please find your Owner Wage Report attached. Reply with any questions and we'll jump on a call." data-testid="tpl-email" />
                        </div>
                        <label className="flex items-center gap-2 text-[12.5px] text-[#0F1115] cursor-pointer">
                            <input type="checkbox" checked={!!editing.is_default} onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} data-testid="tpl-default" />
                            Apply this template automatically for every new report
                        </label>
                    </div>
                ) : loading ? (
                    <div className="py-10 text-center text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin" /></div>
                ) : list.length === 0 ? (
                    <div className="rounded-md border border-dashed border-[#E4E4E7] bg-white p-6 text-center text-[13px] text-[#5E636E]">
                        No templates yet. Create one to stop retyping hours, retained %, and the client email.
                    </div>
                ) : (
                    <div className="rounded-md border border-[#E4E4E7] divide-y divide-[#EEEDE7] bg-white max-h-[55vh] overflow-y-auto">
                        {list.map((t) => (
                            <div key={t.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`tpl-row-${t.id}`}>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-[13px] text-[#0F1115] truncate">{t.name}</span>
                                        {t.is_default && (
                                            <span className="inline-flex items-center gap-0.5 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800">
                                                <Star className="h-2.5 w-2.5" />Default
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11.5px] text-[#5E636E] truncate">
                                        {t.description || `${t.hours_per_week}h · $${t.market_hourly_rate}/hr · ${Math.round((t.retained_profit_pct || 0) * 100)}% retained`}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <Button size="sm" variant="outline" className="h-7 text-[11.5px]" onClick={() => setEditing({ ...t })} data-testid={`tpl-edit-${t.id}`}>Edit</Button>
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-[#5E636E] hover:text-red-700" onClick={() => remove(t)} data-testid={`tpl-delete-${t.id}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <DialogFooter className="gap-2">
                    {editing ? (
                        <>
                            <Button variant="outline" onClick={() => setEditing(null)}>Back</Button>
                            <Button className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" disabled={saving || !editing.name} onClick={save} data-testid="tpl-save">
                                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                                Save template
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={onClose}>Close</Button>
                            <Button className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" onClick={() => setEditing({ ...EMPTY })} data-testid="tpl-new">
                                <Plus className="h-3.5 w-3.5 mr-1" /> New template
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function TplNum({ label, value, onChange, suffix, testid }) {
    return (
        <label className="block">
            <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">{label}</div>
            <div className="relative mt-1">
                <Input value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
                    className="h-8 text-[12.5px] text-right tabular-nums pr-6" data-testid={testid} />
                {suffix && <span className="absolute right-2 top-1.5 text-[11px] text-[#5E636E]">{suffix}</span>}
            </div>
        </label>
    );
}
